import React from "react";
import { FiUpload } from "react-icons/fi";
import { BsFileEarmarkSpreadsheet, BsFileEarmarkText } from "react-icons/bs";
import { FaGraduationCap, FaUsers, FaUserGraduate } from "react-icons/fa";
import { useBulkUpload } from "../hooks/useBulkUpload";
import DataPreview from "./DataPreview";
import ProgressTracker from "./ProgressTracker";
import ResultsSummary from "./ResultsSummary";
import "../BulkUpload.css";

const StudentBulkUpload = ({ corpId, corpName, loadingGif }) => {
  const {
    fileInputRef,
    uploadState,
    file,
    parsedData,
    validationResults,
    uploadProgress,
    uploadResults,
    dragActive,
    isLoading,
    handleDrag,
    handleDrop,
    handleFileInput,
    handleBulkUpload,
    testSingleRecord,
    resetUpload,
    downloadTemplate
  } = useBulkUpload("Student", corpId, corpName);

  return (
    <div className="bulk-upload-container student-bulk-upload">
      {isLoading && (
        <div className="loading-overlay">
          <img src={loadingGif} alt="Loading..." className="loading-gif" />
        </div>
      )}

      <div className="bulk-upload-header student-header">
        <div className="header-icon">
          <FaGraduationCap size={48} color="#007bff" />
        </div>
        <h2>
          <FaUserGraduate className="inline-icon" />
          Student Bulk Registration
        </h2>
        <p className="header-description">
          Upload CSV or Excel files to register multiple students at once. 
          Each student record should include parent/guardian information.
        </p>
        
        <div className="registration-info">
          <div className="info-card">
            <FaUsers className="info-icon" />
            <div className="info-content">
              <h4>School/Institute</h4>
              <p>{corpName}</p>
            </div>
          </div>
        </div>

        <div className="template-download-section">
          <h3>📥 Download Student Template</h3>
          <p>Use our student template with parent information fields:</p>
          <div className="template-buttons">
            <button
              className="template-download-btn student-btn"
              onClick={() => downloadTemplate("Student")}
            >
              📄 Download Student Template (CSV)
            </button>
            <a
              href="/templates/README.md"
              target="_blank"
              rel="noopener noreferrer"
              className="template-help-link"
            >
              📖 View Template Guide
            </a>
          </div>
          <div className="template-info">
            <h4>Student Template Includes:</h4>
            <ul>
              <li>Student personal details (Name, Class)</li>
              <li>Parent/Guardian information (Name, Mobile, Email)</li>
              <li>Address and location details</li>
              <li>Pickup and drop coordinates</li>
            </ul>
          </div>
        </div>
      </div>

      {uploadState === "idle" && (
        <div className="upload-section student-upload">
          <div
            className={`file-drop-zone student-drop-zone ${dragActive ? 'drag-active' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="drop-zone-content">
              <div className="file-icons">
                <BsFileEarmarkSpreadsheet size={48} />
                <BsFileEarmarkText size={48} />
              </div>
              <h3>Drop your student data file here or click to browse</h3>
              <p>Supports CSV, Excel (.xlsx, .xls) files with student and parent information</p>
              <div className="upload-requirements">
                <h4>File Requirements:</h4>
                <ul>
                  <li>Include student name and class information</li>
                  <li>Parent/guardian details are required</li>
                  <li>Valid email addresses for parent communication</li>
                  <li>Mobile numbers should be 9 digits (without country code)</li>
                </ul>
              </div>
              <FiUpload size={24} />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}

      {uploadState === "parsing" && (
        <div className="parsing-section student-parsing">
          <div className="parsing-indicator">
            <div className="spinner student-spinner"></div>
            <h3>Processing student data...</h3>
            <p>Validating student and parent information from {file?.name}</p>
            <div className="parsing-steps">
              <div className="step">✓ Reading file format</div>
              <div className="step">⏳ Validating student records</div>
              <div className="step">⏳ Checking parent information</div>
              <div className="step">⏳ Preparing preview</div>
            </div>
          </div>
        </div>
      )}

      {uploadState === "preview" && (
        <DataPreview
          data={parsedData}
          validationResults={validationResults}
          type="Student"
          onUpload={handleBulkUpload}
          onTestSingle={testSingleRecord}
          onCancel={resetUpload}
          fileName={file?.name}
        />
      )}

      {uploadState === "uploading" && (
        <ProgressTracker
          progress={uploadProgress}
          type="Student"
        />
      )}

      {uploadState === "complete" && (
        <ResultsSummary
          results={uploadResults}
          type="Student"
          onReset={resetUpload}
        />
      )}
    </div>
  );
};

export default StudentBulkUpload;
